import { Popover as PopoverPrimitive } from 'radix-ui';
import React, { type ComponentPropsWithoutRef } from 'react';
import { portaledSurfaceVariants, type PortaledSurfaceVariant } from '../lib/portaledSurface';
import { cn } from '../lib/utils';
import { usePortalContainer } from './PortalContainer';
import { PopoverContent as PopoverContentContent } from './ui/popover';

type PopoverContentProps = ComponentPropsWithoutRef<typeof PopoverContentContent> & {
  /**
   * Where the popover portals to. Defaults to the `PortalContainerProvider` target,
   * and then to `document.body`.
   */
  container?: Element | null;
  /** Light (default) or dark surface. Pass `"dark"` for every popover opened from inside a `DarkThemeScope` — it portals to `document.body` and so escapes the scope's own `.dark`. */
  variant?: PortaledSurfaceVariant;
};

export const PopoverContent: React.FC<PopoverContentProps> = ({
  container,
  variant = 'light',
  className,
  align = 'center',
  sideOffset = 4,
  ...props
}) => {
  const providedContainer = usePortalContainer();
  const portalContainer = container ?? providedContainer;
  const contentClassName = cn(
    'tw max-h-[var(--radix-popper-available-height)] border-border-muted',
    portaledSurfaceVariants({ variant }),
    className,
  );

  if (portalContainer) {
    return (
      <PopoverPrimitive.Portal container={portalContainer}>
        <PopoverPrimitive.Content
          {...props}
          data-slot="popover-content"
          align={align}
          sideOffset={sideOffset}
          className={cn(
            'origin-(--radix-popover-content-transform-origin) outline-hidden z-50 w-72 rounded-md border bg-popover p-4 text-popover-foreground shadow-md data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2',
            contentClassName,
          )}
        />
      </PopoverPrimitive.Portal>
    );
  }

  return (
    <PopoverContentContent
      {...props}
      align={align}
      sideOffset={sideOffset}
      className={contentClassName}
    />
  );
};

export { Popover, PopoverAnchor, PopoverTrigger } from './ui/popover';
